import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { FC } from 'react';
import { useHealthAndSafetyState } from '../../../../contexts/healthAndSafety.context';

const HealthCheckupDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedCheckup } = useHealthAndSafetyState();

  if (!selectedCheckup) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="health-checkup-details-container"
      >
        <Typography>
          {translateText('administration.healthAndSafety.noData')}
        </Typography>
      </Box>
    );
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  const handleDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="health-checkup-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.healthAndSafety.healthCheckupsDetails')}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('common.description')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-type">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.checkupType'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCheckup.checkupType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-person-type">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.personType'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCheckup.personType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-person-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.personId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCheckup.personId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-doctor">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.healthAndSafety.columns.doctor')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCheckup.doctorName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-hospital">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.hospitalClinic'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCheckup.hospitalClinic}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.healthAndSafety.columns.status')}
              </Typography>
              <Chip
                label={
                  selectedCheckup.isCleared
                    ? translateText(
                        'administration.healthAndSafety.status.cleared'
                      )
                    : translateText(
                        'administration.healthAndSafety.status.notCleared'
                      )
                }
                color={selectedCheckup.isCleared ? 'success' : 'warning'}
                size="small"
              />
            </Grid>
            {selectedCheckup.findings && (
              <Grid item xs={12} data-testid="checkup-findings">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.findings'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedCheckup.findings}
                </Typography>
              </Grid>
            )}
            {selectedCheckup.recommendations && (
              <Grid item xs={12} data-testid="checkup-recommendations">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.recommendations'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedCheckup.recommendations}
                </Typography>
              </Grid>
            )}
            {selectedCheckup.notes && (
              <Grid item xs={12} data-testid="checkup-notes">
                <Typography variant="caption" color="textSecondary">
                  {translateText('common.remarks')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedCheckup.notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.datesTimeline'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-date">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.checkupDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedCheckup.checkupDate)}
              </Typography>
            </Grid>
            {selectedCheckup.nextCheckupDate && (
              <Grid item xs={12} sm={6} md={4} data-testid="checkup-next-date">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.columns.nextCheckup'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedCheckup.nextCheckupDate)}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4} data-testid="checkup-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.checkupId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedCheckup.id}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedCheckup.attachments &&
        selectedCheckup.attachments.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {translateText(
                  'administration.healthAndSafety.labels.attachments'
                )}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List data-testid="checkup-attachments">
                {selectedCheckup.attachments.map((attachment, index) => (
                  <ListItem
                    key={attachment.documentId || index}
                    sx={{ pl: 0 }}
                    secondaryAction={
                      attachment.fileUrl && (
                        <IconButton
                          edge="end"
                          aria-label="download"
                          onClick={() =>
                            handleDownload(
                              attachment.fileUrl!,
                              attachment.documentName
                            )
                          }
                          data-testid={`checkup-attachment-download-${index}`}
                        >
                          <DownloadIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemText
                      primary={
                        attachment.documentName ||
                        attachment.description ||
                        `Document ${index + 1}`
                      }
                      secondary={
                        attachment.description && attachment.documentName
                          ? attachment.description
                          : undefined
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}
    </Box>
  );
};

export default HealthCheckupDetailsView;
